import { apiClient } from "@/api/apiClient"
import { paramsSerializerV0 } from "@/api/client"
import type { ApiResultPromise } from "@/api/errors"
import type { List } from "@/api/lists"
import type { User } from "@/api/users"
import type { EmptyObject } from "@/util/types"

import type { ProductType } from "./shared"

export interface DenormalizedWorkspace {
  id: number
  name: string
  productType: ProductType
  isPublic: boolean
  isDefault: boolean

  description?: string
  color?: string
  containingWorkspaceId?: number | null
  customMetadata?: { [index: string]: string | number | boolean | null }
  availableTemplateIds?: number[] | null
  listsTemplateIds?: number[]
  ancestors?: Array<{
    id: number
    name: string
  }>

  createdAt: string
  creator?: User

  // == Derived ==
  lists: List[]
  listsCount: number
  fileFeedsCount: number
  workflowsCount: number
}

export type Workspace = Omit<DenormalizedWorkspace, "lists"> & {
  listIds: number[]
}

interface GetWorkspacesRequestParams {
  productType: ProductType
  page?: number
  pageSize?: number
  containingWorkspaceId?: number | null
  searchTerm?: string
  sortModel?: {
    colId: string
    sort: string
  }
}

interface GetWorkspacesResponse {
  workspaces: DenormalizedWorkspace[]
  count: number
}

export async function getWorkspaces(
  params: GetWorkspacesRequestParams,
  signal?: AbortSignal,
): ApiResultPromise<GetWorkspacesResponse> {
  // paramsSerializerV0 drops nulls, so keep the root-only key alive as `=`.
  // The backend coerces the empty value back to nil and takes the root branch.
  const requestParams =
    params.containingWorkspaceId === null
      ? { ...params, containingWorkspaceId: "" }
      : params

  return apiClient.get<GetWorkspacesResponse>(
    "/api/workspaces",
    "Failed to load workspaces",
    {
      params: requestParams,
      paramsSerializer: paramsSerializerV0,
      signal,
    },
  )
}

/**
 * Fetches the denormalized workspaces array that is normally inlined into
 * viewData under the `workspaces` key. Used to lazy-hydrate the Redux store
 * after first paint when the bootstrap blob is slimmed (SlimViewData). The
 * response shape is byte-identical to the viewData value, so it flows through
 * the same `normalizeWorkspaces` normalization path.
 */
export async function getViewDataWorkspaces(): ApiResultPromise<DenormalizedWorkspace[]> {
  return apiClient.get<DenormalizedWorkspace[]>(
    "/api/workspaces/view-data",
    "Failed to load workspaces",
  )
}

// Slightly different naming convention to avoid confusion with the getWorkspaceById function.
export async function fetchWorkspaceById(
  workspaceId: number,
): ApiResultPromise<DenormalizedWorkspace> {
  return apiClient.get<DenormalizedWorkspace>(
    `/api/workspaces/${workspaceId}`,
    "Failed to load workspace",
  )
}

type MutateWorkspace = Omit<
  Workspace,
  | "isPublic" // Not supported yet
  | "isDefault" // Immutable
  | "createdAt"
  | "creator"
  | "lists"
  | "listsCount"
  | "fileFeedsCount"
  | "workflowsCount"
>

type CreateWorkspace = Partial<MutateWorkspace> &
  Pick<MutateWorkspace, "name" | "productType">

export async function postWorkspace(
  workspace: CreateWorkspace,
): ApiResultPromise<DenormalizedWorkspace> {
  return apiClient.post<DenormalizedWorkspace>(
    "/api/workspaces",
    "Failed to create workspace",
    {
      ...workspace,
      // TODO: Allow non-public workspaces
      isPublic: true,
    },
  )
}

export type UpdateWorkspace = Omit<Partial<MutateWorkspace>, "productType">

export async function putWorkspace(
  workspaceId: number,
  workspace: UpdateWorkspace,
): ApiResultPromise<DenormalizedWorkspace> {
  return apiClient.put<DenormalizedWorkspace>(
    `/api/workspaces/${workspaceId}`,
    "Failed to update workspace",
    workspace,
  )
}

export async function moveWorkspace(
  workspaceId: number,
  containingWorkspaceId: number | null,
): ApiResultPromise<DenormalizedWorkspace> {
  return apiClient.patch<DenormalizedWorkspace>(
    `/api/workspaces/${workspaceId}`,
    "Failed to move workspace",
    {
      containingWorkspaceId,
    },
  )
}

export async function deleteWorkspace(workspaceId: number): ApiResultPromise<Workspace> {
  return apiClient.delete<Workspace>(
    `/api/workspaces/${workspaceId}`,
    "Failed to delete workspace",
  )
}

export async function getWorkspaceLists(
  workspaceId: number,
): ApiResultPromise<{ lists: Array<List> }> {
  return apiClient.get<{ lists: Array<List> }>(
    `/api/workspaces/${workspaceId}/lists`,
    "Failed to load workspace sheets",
  )
}

export async function moveListToWorkspace(
  workspaceId: number,
  listId: number,
  newWorkspaceId: number,
): ApiResultPromise<EmptyObject> {
  return apiClient.post<EmptyObject>(
    `/api/workspaces/${workspaceId}/lists/${listId}/move-to-workspace`,
    "Failed to move sheet",
    { newWorkspaceId },
  )
}

export async function migrateWorkspaceWorkflowsToV2(
  workspaceId: number,
): ApiResultPromise<{ message: string }> {
  return apiClient.post<{ message: string }>(
    `/api/workspaces/${workspaceId}/migrate-workflows-to-v2`,
    "Failed to migrate workflows",
  )
}
